import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Progress,
  Timeline,
  Tag,
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  Statistic,
  Spin,
  Divider,
  Table,
  DatePicker,
  TimePicker,
  Tooltip,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  ScheduleOutlined,
  PrinterOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getJobsByStage,
  getAllJobs,
  getJobById,
  getInstructionsForJob,
  generateInstruction,
  completeInstruction,
  printInstruction,
  scheduleJob,
  getJobRequirements,
  getJobWastageSummary,
  getOutsourceForJob,
  getAllMachines,
  advanceJobStage,
  moveJobToStage,
} from "../../api/jobApi";
import JobStatusBadge from "./JobStatusBadge";
import WorkInstructionsPanel from "./WorkInstructionsPanel";
import MachineSelectionPanel from "./MachineSelectionPanel";
import StageConfigurator from "./StageConfigurator";
import JobSpecsPanel from "./JobSpecsPanel";
import { FulfillmentBadge, JobRequirements } from "../mrp";
import BatchInstructionsPanel from "../batch-planning/BatchInstructionsPanel";
import { getBoxSizeSuggestion } from "../../api/batchPlanningApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const JobDetail = ({ jobId: propJobId, isModal = false, onClose }) => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── Route ──────────────────────────────────────────────────
  const params = useParams();
  const id = propJobId || params.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── State ──────────────────────────────────────────────────
  const [job, setJob] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [requirements, setRequirements] = useState(null);
  const [wastageSummary, setWastageSummary] = useState(null);
  const [outsourceJobs, setOutsourceJobs] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [boxSizeSuggestion, setBoxSizeSuggestion] = useState(null);

  // Modals
  const [outputModalVisible, setOutputModalVisible] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);

  // Forms
  const [outputForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [generateForm] = Form.useForm();

  // ── Fetch Data ─────────────────────────────────────────────
  useEffect(() => {
    fetchJobData();
  }, [id]);

  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "schedule") setScheduleModalVisible(true);
    if (action === "instruction") setGenerateModalVisible(true);
  }, [searchParams]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  const fetchJobData = async () => {
    setLoading(true);
    try {
      const [found, instrData] = await Promise.all([
        getJobById(id),
        getInstructionsForJob(id).catch(() => []),
      ]);

      if (!found) {
        toast.error("Job not found");
        navigate("/job");
        return;
      }
      setJob(found);
      setInstructions(Array.isArray(instrData) ? instrData : instrData?.data || []);

      // Load supplementary data in parallel (non-blocking)
      Promise.allSettled([
        getJobRequirements(id),
        getJobWastageSummary(id),
        getOutsourceForJob(id),
        getAllMachines({ status: "operational" }),
        getBoxSizeSuggestion(id),
      ]).then(([reqRes, wastRes, outRes, machRes, bssRes]) => {
        if (reqRes.status === "fulfilled") setRequirements(reqRes.value);
        if (wastRes.status === "fulfilled") setWastageSummary(wastRes.value);
        if (outRes.status === "fulfilled") setOutsourceJobs(outRes.value);
        if (machRes.status === "fulfilled")
          setMachines(
            Array.isArray(machRes.value) ? machRes.value : machRes.value?.data || []
          );
        if (bssRes.status === "fulfilled") setBoxSizeSuggestion(bssRes.value || null);
      });
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to load job details");
      navigate("/job");
    } finally {
      setLoading(false);
    }
  };

  // ── Actions ────────────────────────────────────────────────

  // Generate instruction for a stage
  const handleGenerateInstruction = async (values) => {
    try {
      setActionLoading(true);
      await generateInstruction({
        job_id: id,
        stage_code: values.stage_code,
      });
      toast.success("Instruction generated successfully");
      setGenerateModalVisible(false);
      generateForm.resetFields();
      // Refresh instructions
      const instrData = await getInstructionsForJob(id).catch(() => []);
      setInstructions(Array.isArray(instrData) ? instrData : instrData?.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate instruction"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Record output on an instruction (complete it)
  const handleRecordOutput = async (values) => {
    try {
      setActionLoading(true);
      await completeInstruction(selectedInstruction.id, {
        actual_output: values.actual_output,
        rejected_qty: values.rejected_qty || 0,
        wastage_qty: values.wastage_qty || 0,
        notes: values.notes,
      });
      toast.success("Output recorded — instruction completed");
      setOutputModalVisible(false);
      outputForm.resetFields();
      setSelectedInstruction(null);
      fetchJobData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to record output"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Schedule job to machine + date
  const handleSchedule = async (values) => {
    try {
      setActionLoading(true);
      await scheduleJob({
        job_id: id,
        stage_id: values.stage_id,
        machine_id: values.machine_id,
        scheduled_date: values.scheduled_date.format("YYYY-MM-DD"),
        start_time: values.start_time.format("HH:mm"),
      });
      toast.success("Job scheduled successfully");
      setScheduleModalVisible(false);
      scheduleForm.resetFields();
      fetchJobData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to schedule job"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Print an instruction
  const handlePrintInstruction = async (instrId) => {
    try {
      const data = await printInstruction(instrId);
      // Open in new tab / print dialog
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(
          `<html><head><title>Instruction</title></head><body>
            <pre>${JSON.stringify(data, null, 2)}</pre>
            <script>window.print();</script>
          </body></html>`
        );
        printWindow.document.close();
      }
    } catch (error) {
      toast.error("Failed to load print data");
    }
  };

  // ── Instruction timeline icon ──────────────────────────────
  const getInstructionIcon = (instr) => {
    switch (instr.status) {
      case "COMPLETED":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "PENDING":
        return <CloseCircleOutlined style={{ color: "#faad14" }} />;
      default:
        return <FileTextOutlined style={{ color: "#1890ff" }} />;
    }
  };

  // ── Loading / Not found ────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!job) return null;

  const totalQty = job.production_quantity || 0;
  const doneQty = job.completed_quantity || 0;
  const remainingQty = Math.max(0, totalQty - doneQty);
  const progressPct = totalQty > 0 ? Math.round((doneQty / totalQty) * 100) : 0;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      {!isModal && <PageTitle title="Back" subtitle={`Job ${job.job_number}`} />}

      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Title level={4} style={{ margin: 0 }}>
                {job.job_number}
              </Title>
              <JobStatusBadge status={job.status} />
              {job.priority && (
                <Tag
                  color={
                    { urgent: "red", high: "orange", normal: "blue", low: "default" }[
                      job.priority
                    ] || "default"
                  }
                >
                  {job.priority}
                </Tag>
              )}
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {(job.current_stage === "order_received" || job.current_stage === "pending_inventory") && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={actionLoading}
                  onClick={async () => {
                    try {
                      setActionLoading(true);
                      const result = await advanceJobStage(id);
                      toast.success(result.message);
                      fetchJobData();
                    } catch (err) {
                      toast.error(err.response?.data?.message || "Failed to advance stage");
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                  style={job.current_stage === "pending_inventory" ? { background: "#fa8c16", borderColor: "#fa8c16" } : {}}
                >
                  {job.current_stage === "order_received" ? "Check Inventory & Advance" : "Re-check Inventory"}
                </Button>
              )}
              <Button
                icon={<ScheduleOutlined />}
                onClick={() => setScheduleModalVisible(true)}
              >
                Schedule
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => setGenerateModalVisible(true)}
              >
                Generate Instruction
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        {/* ── Main Content ──────────────────────────────────── */}
        <Col xs={24} lg={16}>
          {/* Job Details */}
          <Card title="Job Details" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Job Number" span={1}>
                {job.job_number}
              </Descriptions.Item>
              <Descriptions.Item label="Order" span={1}>
                <Link to={`/order/${job.order?.id}`}>
                  {job.order?.order_number || "-"}
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="Customer" span={1}>
                {job.order?.customer?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Expected Delivery" span={1}>
                {job.expected_delivery
                  ? moment(job.expected_delivery).format("DD MMM YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Item Name" span={2}>
                <Text strong>{job.item_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Subcategory" span={1}>
                {job.subcategory || job.order_item?.subcategory || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Dimensions" span={1}>
                {job.length && job.width
                  ? `${job.length} × ${job.width}${job.height ? ` × ${job.height}` : ""} ${job.unit_type || ""}`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Ply / Casting" span={1}>
                {job.ply ? `${job.ply} Ply` : "-"}
                {job.casting_type ? ` · ${job.casting_type}` : ""}
              </Descriptions.Item>
              <Descriptions.Item label="RS / CS" span={1}>
                <Space size={4}>
                  {job.roll_size > 0 && <Tag>{job.roll_size}"</Tag>}
                  {job.cutting_size > 0 && <Tag>{job.cutting_size}"</Tag>}
                  {job.boxes_per_sheet && <Text type="secondary" style={{ fontSize: 11 }}>M={job.boxes_per_sheet}</Text>}
                  {job.sheets_per_box && <Text type="secondary" style={{ fontSize: 11 }}>N={job.sheets_per_box}</Text>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Joining" span={1}>
                {job.joining_method || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Printing" span={1}>
                {job.is_printing ? (() => {
                  let pc = job.printing_config;
                  if (typeof pc === "string") { try { pc = JSON.parse(pc); } catch(e) { pc = null; } }
                  return pc
                    ? `${pc.printingType || pc.type || "Yes"} · ${pc.printingColors || pc.colors || ""} colour · ${pc.printingSides || pc.sides || ""} side`
                    : "Yes";
                })() : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Coating" span={1}>
                {job.is_coating ? (() => {
                  let cc = job.coating_config;
                  if (typeof cc === "string") { try { cc = JSON.parse(cc); } catch(e) { cc = null; } }
                  return cc?.type || cc?.coatingType || "Yes";
                })() : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Current Stage" span={1}>
                {job.applicable_stages?.length > 0 ? (
                  <Select
                    value={job.current_stage}
                    style={{ minWidth: 180 }}
                    size="small"
                    onChange={async (value) => {
                      try {
                        setActionLoading(true);
                        const result = await moveJobToStage(id, value);
                        toast.success(result.message);
                        fetchJobData();
                      } catch (err) {
                        toast.error(err.response?.data?.message || "Failed to move stage");
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    loading={actionLoading}
                  >
                    {job.applicable_stages.map((stage) => (
                      <Option key={stage} value={stage}>
                        {stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Tag>{job.current_stage?.replace(/_/g, " ")}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Material Status" span={1}>
                {job.fulfillment_status ? <FulfillmentBadge status={job.fulfillment_status} /> : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Batch" span={1}>
                {job.batch_number
                  ? <Tag color="purple">{job.batch_number}</Tag>
                  : <Tag color="default">Not batched</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Production Qty" span={1}>
                {(job.production_quantity || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Completed" span={1}>
                {(job.completed_quantity || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Box Weight" span={1}>
                {job.box_weight ? `${job.box_weight} kg` : "-"}<span style={{ display: "none" }}>
                {job.box_weight ? ` · ${job.box_weight} kg` : ""}
              </span></Descriptions.Item>
            </Descriptions>

            {/* Layers table — inline, no extra click needed */}
            {(() => {
              let layers = job.layers || job.order_item?.layers;
              if (typeof layers === "string") { try { layers = JSON.parse(layers); } catch(e) { layers = []; } }
              if (!Array.isArray(layers) || layers.length === 0) return null;
              return (
                <>
                  <Divider orientation="left" style={{ margin: "12px 0 8px", fontSize: 13 }}>Layer Configuration</Divider>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#fafafa" }}>
                        {["#", "Position", "Paper Type", "GSM", "BF", "Flute"].map((h) => (
                          <th key={h} style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {layers.map((layer, idx) => (
                        <tr key={idx}>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>{idx + 1}</td>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>{layer.layerType || "-"}</td>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>{layer.materialType || layer.paper_type || "-"}</td>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", fontWeight: 600 }}>{layer.gsm || "-"}</td>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>{layer.bf || "-"}</td>
                          <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                            {layer.layerType === "flute" ? (layer.ff ? `FF: ${layer.ff}` : "Flute") : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              );
            })()}
          </Card>

          {/* Box size RS reduction suggestion */}
          {boxSizeSuggestion && (
            <Card
              size="small"
              style={{ marginBottom: 16, borderLeft: "3px solid #fa8c16" }}
              styles={{ body: { padding: "10px 16px" } }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <Space>
                  <Text strong style={{ color: "#fa8c16" }}>💡 RS Reduction Opportunity</Text>
                  <Tag color="orange">
                    {boxSizeSuggestion.current_rs}" → {boxSizeSuggestion.target_rs}" (save 2")
                  </Tag>
                </Space>
                <Text style={{ fontSize: 12 }}>
                  W+H is <Text strong>{boxSizeSuggestion.overshoot_inches}"</Text> ({boxSizeSuggestion.overshoot_soothes} soothes) above the lower RS boundary.
                  Reducing dimensions by <Text strong>{boxSizeSuggestion.reduction_inches}"</Text> ({boxSizeSuggestion.reduction_soothes} soothes) drops the RS by 2".
                </Text>
                <Space size={6} wrap>
                  {boxSizeSuggestion.options.map((opt, i) => (
                    <Tag key={i} color="orange" style={{ fontSize: 11 }}>
                      {opt.dimension === "Split W+H"
                        ? `Split: W ${opt.from_W}"→${opt.to_W}" · H ${opt.from_H}"→${opt.to_H}" (${opt.reduction_soothes_each}s each)`
                        : `${opt.dimension}: ${opt.from}"→${opt.to}" (${opt.reduction_soothes} soothes)`}
                    </Tag>
                  ))}
                </Space>
              </Space>
            </Card>
          )}

          {/* Job Specifications - Editable auto-calculated values */}
          <JobSpecsPanel
            job={job}
            onUpdate={fetchJobData}
          />

          {/* Machine Selection Plan */}
          <MachineSelectionPanel
            jobId={id}
            selectedMachines={job.selected_machines}
            onUpdate={fetchJobData}
          />

          {/* Production Instructions — Rich Panel */}
          <WorkInstructionsPanel
            jobId={id}
            selectedMachines={job.selected_machines}
            onInstructionChange={fetchJobData}
          />

          {/* Material Requirements (MRP) */}
          <JobRequirements jobId={id} jobNumber={job.job_number} />

          {/* Batch Instructions */}
          <BatchInstructionsPanel jobId={id} />

          {/* Wastage Summary */}
          {wastageSummary && wastageSummary.job && (
            <Card
              title={
                <Space>
                  <WarningOutlined />
                  <span>Wastage Summary</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Total Wastage"
                    value={wastageSummary.job.total_wastage || 0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Wastage %"
                    value={wastageSummary.job.wastage_pct || 0}
                    suffix="%"
                    precision={2}
                    valueStyle={{
                      color:
                        (wastageSummary.job.wastage_pct || 0) > 3
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Buffer Allocated"
                    value={wastageSummary.buffer_utilization?.buffer_allocated || 0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Buffer Remaining"
                    value={wastageSummary.buffer_utilization?.buffer_remaining || 0}
                    valueStyle={{
                      color:
                        (wastageSummary.buffer_utilization?.buffer_remaining || 0) <= 0
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  />
                </Col>
              </Row>
            </Card>
          )}
        </Col>

        {/* ── Sidebar ───────────────────────────────────────── */}
        <Col xs={24} lg={8}>
          {/* Progress */}
          <Card title="Production Progress" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Completed"
                  value={doneQty.toLocaleString()}
                  suffix={`/ ${totalQty.toLocaleString()}`}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Remaining"
                  value={remainingQty.toLocaleString()}
                  valueStyle={{
                    color: remainingQty > 0 ? "#faad14" : "#52c41a",
                  }}
                />
              </Col>
            </Row>
            <Progress
              percent={progressPct}
              status={progressPct >= 100 ? "success" : "active"}
              style={{ marginTop: 16 }}
            />
          </Card>

          {/* Applicable Stages Flow — Editable */}
          {job?.applicable_stages && job.applicable_stages.length > 0 && (
            <StageConfigurator job={job} onUpdate={fetchJobData} />
          )}

          {/* Outsource Jobs */}
          {outsourceJobs && outsourceJobs.total > 0 && (
            <Card title="Outsource Jobs" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="Total" value={outsourceJobs.total} />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Pending"
                    value={outsourceJobs.pending || 0}
                    valueStyle={{ color: "#faad14" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Done"
                    value={outsourceJobs.completed || 0}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* Quick Links */}
          <Card title="Quick Actions" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {(job.current_stage === "order_received" || job.current_stage === "pending_inventory") && (
                <Button
                  block
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={actionLoading}
                  onClick={async () => {
                    try {
                      setActionLoading(true);
                      const result = await advanceJobStage(id);
                      toast.success(result.message);
                      fetchJobData();
                    } catch (err) {
                      toast.error(err.response?.data?.message || "Failed to advance stage");
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                  style={job.current_stage === "pending_inventory" ? { background: "#fa8c16", borderColor: "#fa8c16" } : {}}
                >
                  {job.current_stage === "order_received" ? "Check Inventory & Advance" : "Re-check Inventory"}
                </Button>
              )}
              <Button
                block
                icon={<ScheduleOutlined />}
                onClick={() => setScheduleModalVisible(true)}
              >
                Schedule to Machine
              </Button>
              <Button
                block
                icon={<FileTextOutlined />}
                onClick={() => setGenerateModalVisible(true)}
              >
                Generate Instruction
              </Button>
              <Button
                block
                icon={<ExperimentOutlined />}
                onClick={() => navigate("/production/kanban")}
              >
                View Kanban Board
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ── Record Output Modal ─────────────────────────────── */}
      <Modal
        title={`Record Output — ${selectedInstruction?.stage_name || ""}`}
        open={outputModalVisible}
        onCancel={() => {
          setOutputModalVisible(false);
          outputForm.resetFields();
          setSelectedInstruction(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={outputForm}
          layout="vertical"
          onFinish={handleRecordOutput}
          initialValues={{
            actual_output: remainingQty,
            rejected_qty: 0,
            wastage_qty: 0,
          }}
        >
          <Form.Item
            name="actual_output"
            label="Actual Output (good pieces)"
            rules={[
              { required: true, message: "Enter actual output" },
              { type: "number", min: 0, message: "Must be ≥ 0" },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="rejected_qty" label="Rejected Quantity">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="wastage_qty" label="Wastage Quantity">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Any notes about this stage..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                Record Output
              </Button>
              <Button
                onClick={() => {
                  setOutputModalVisible(false);
                  setSelectedInstruction(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Schedule Modal ──────────────────────────────────── */}
      <Modal
        title="Schedule Job"
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          scheduleForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={scheduleForm} layout="vertical" onFinish={handleSchedule}>
          <Form.Item
            name="stage_id"
            label="Stage"
            rules={[{ required: true, message: "Select a stage" }]}
          >
            <Select placeholder="Select stage">
              {(job.flow_stages || []).map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
              {/* Fallback: if we have current stage info */}
              {(!job.flow_stages || job.flow_stages.length === 0) &&
                job.current_stage && (
                  <Option value={job.current_stage}>
                    {job.current_stage_name || job.current_stage}
                  </Option>
                )}
            </Select>
          </Form.Item>

          <Form.Item
            name="machine_id"
            label="Machine"
            rules={[{ required: true, message: "Select a machine" }]}
          >
            <Select placeholder="Select machine" showSearch optionFilterProp="children">
              {machines.map((m) => (
                <Option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduled_date"
            label="Date"
            rules={[{ required: true, message: "Pick a date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="Start Time"
            rules={[{ required: true, message: "Pick start time" }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                Schedule
              </Button>
              <Button onClick={() => setScheduleModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Generate Instruction Modal ──────────────────────── */}
      <Modal
        title="Generate Production Instruction"
        open={generateModalVisible}
        onCancel={() => {
          setGenerateModalVisible(false);
          generateForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={handleGenerateInstruction}
        >
          <Form.Item
            name="stage_code"
            label="Stage"
            rules={[{ required: true, message: "Select a stage" }]}
            extra="Supported: corrugation, sheet_cutting, pasting, printing, casting, joining"
          >
            <Select placeholder="Select production stage">
              <Option value="corrugation">Corrugation</Option>
              <Option value="sheet_cutting">Sheet Cutting</Option>
              <Option value="pasting">Pasting</Option>
              <Option value="printing">Printing</Option>
              <Option value="casting">Casting (Die / Rotary)</Option>
              <Option value="joining">Joining</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                Generate
              </Button>
              <Button onClick={() => setGenerateModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default JobDetail;
